import { ref, watch, WatchStopHandle } from 'vue'
import { RouteLocationRaw } from 'vue-router'

import {
  CTX,
  LogLevel,
  Shell,
  TRepository,
} from '@sennder/microfrontend-registry'
import { IOttersSharedData } from '@sennder/senn-node-microfrontend-interfaces'
import { MicroFrontendLogger } from '@sennder/shell-utilities'
import { deepToRaw } from '@sennder/shell-utilities'

import { getRouteLocationName } from '@/modules/utils'
import router from '@/router'
import { getMicrofrontendData } from '@/store'
import { AppAnalyticsProvider } from '@/services/analyticsProvider'
import { loggerInstance } from '@/services/logger'
import { getStateCallbacks, getStateProviders } from '@/store'
import { routes } from './routes'
import { ENV } from '@/common/config'

export const shellLogLevel = ENV === 'dev' ? LogLevel.ALL : LogLevel.OFF

export const shell = ref<Shell<IOttersSharedData>>()

export const getRegistryMfContext = async (
  mf: TRepository
): Promise<Readonly<CTX<IOttersSharedData>>> => {
  const routeName = getRouteLocationName(router.currentRoute.value)

  const route = Object.values(routes).find((r) => r.name === routeName)

  const npmName = mf.meta.fml.npmName

  const logger: MicroFrontendLogger = new MicroFrontendLogger(
    {
      codeOwners: mf.meta.codeOwners,
      module: npmName,
    },
    () => loggerInstance
  )

  const analyticsProvider: AppAnalyticsProvider = new AppAnalyticsProvider(
    route?.context.analytics || {
      module: npmName,
      submodule: npmName,
    }
  )

  const context: CTX<IOttersSharedData> = {
    data: deepToRaw(getMicrofrontendData(npmName)),
    callbacks: {
      ...getStateCallbacks(),
      // The store only holds a no-op stub - shell-utilities v9 used to inject
      // the real implementation inside createMfWrapper. The v10 wrapper does
      // not, so the shell has to supply it here or µ-frontend navigation
      // never propagates back to the shell router.
      syncParentRouter: async (route: RouteLocationRaw) => {
        const currentPath = router.currentRoute.value.fullPath
        if (
          (typeof route === 'string' && currentPath === route) ||
          (typeof route === 'object' &&
            currentPath === (route as { path?: string }).path)
        ) {
          return
        }
        await router.push(route)
      },
    },
    providers: {
      ...getStateProviders(),
      logger,
      analytics: analyticsProvider,
    },
    hooks: [
      (_, mfInstance) => {
        /* TODO: what's the better way to discover "pages"?
          Currently a lot of widgets still return syncChildRouter for no good reason
         */
        const isPage = !!mfInstance?.syncChildRouter

        const watchHandles: WatchStopHandle[] = []

        if (isPage) {
          // sync child router with parent router
          watchHandles.push(
            watch(
              () => router.currentRoute.value,
              (to) => {
                mfInstance?.syncChildRouter?.(to.fullPath)
              },
              {
                immediate: true,
              }
            )
          )
        }

        return () => {
          if (watchHandles.length) {
            for (const unwatch of watchHandles) {
              unwatch()
            }
            watchHandles.length = 0
          }
        }
      },
    ],
  }

  return context
}
